import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { api } from "@/lib/api";
import { imageUrl } from "@/components/ImageUpload";
import { Search, Compass, Gift, Sparkles, Star, BadgeCheck, Trophy } from "lucide-react";
import { SEOHead, JsonLd } from "@/components/SEO";

const RADIUS_OPTIONS = [10, 25, 50, 100, 250, 500];

export default function Vendors() {
    const [vendors, setVendors] = useState([]);
    const [categories, setCategories] = useState([]);
    const [boothElements, setBoothElements] = useState([]);
    const [directory, setDirectory] = useState({ categories: [], cities: [] });
    const [filters, setFilters] = useState({ q: "", category_id: "", near: "", radius_miles: 50 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = async () => {
        setLoading(true);
        setError("");
        const params = Object.fromEntries(
            Object.entries(filters).filter(([, v]) => v !== "" && v !== null && v !== undefined)
        );
        try {
            const res = await api.get("/vendors/public", { params });
            setVendors(res.data);
        } catch (err) {
            // Mobile networks + cold-start delays can leave the page stuck
            // on "Loading…" forever without this. Always release the loading
            // state in `finally` + show a retry button.
            setError(err?.response?.data?.detail || "Couldn't load vendors. Check your connection and try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const [c, be, dir] = await Promise.all([
                    api.get("/categories"),
                    api.get("/booth-elements"),
                    api.get("/seo/directory"),
                ]);
                setCategories(c.data);
                setBoothElements(be.data);
                setDirectory(dir.data);
            } catch {
                // Non-critical metadata — vendor list still tries to load below
                // so the page isn't bricked if /seo/directory is slow.
            }
            load();
        })();
        /* eslint-disable-next-line */
    }, []);

    const catName = (id) => categories.find((c) => c.id === id)?.name || id;
    const elementName = (id) => boothElements.find((el) => el.id === id)?.name;

    return (
        <Shell>
            <SEOHead
                title="Healthcare & wellness vendors for corporate health fairs"
                description="Browse vetted healthcare and wellness vendors ready to participate in your next corporate health fair. Filter by category, location, and trust signals."
            />
            <JsonLd data={{
                "@context": "https://schema.org",
                "@type": "CollectionPage",
                name: "Healthcare & wellness vendors",
                description: "Curated marketplace of healthcare and wellness vendors for corporate health fairs.",
                provider: { "@type": "Organization", name: "HealthFairX" },
            }} />
            <section className="bg-brand-bone border-b border-brand-line">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
                    <div className="label-base">Marketplace</div>
                    <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight">Healthcare & wellness vendors</h1>
                    <p className="mt-3 text-brand-mute max-w-2xl">Browse vetted vendors ready to participate in corporate health fairs. Filter by category or search nearby.</p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid lg:grid-cols-12 gap-8">
                <aside className="lg:col-span-3" data-testid="vendor-filters">
                    <div className="rounded-xl border border-brand-line bg-white p-5 sticky top-24">
                        <div className="font-heading font-bold mb-4 flex items-center gap-2"><Search size={16} /> Filters</div>
                        <div className="space-y-4">
                            <div>
                                <label className="label-base">Search</label>
                                <input
                                    data-testid="vendor-search"
                                    className="input-base"
                                    placeholder="Business name"
                                    value={filters.q}
                                    onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                                    onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                                />
                            </div>
                            <div>
                                <label className="label-base flex items-center gap-1"><Compass size={12} /> Near</label>
                                <input
                                    data-testid="vendor-near"
                                    className="input-base"
                                    placeholder="City, state, or zip"
                                    value={filters.near}
                                    onChange={(e) => setFilters({ ...filters, near: e.target.value })}
                                    onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                                />
                                <p className="text-xs text-brand-mute mt-1.5">Matches a vendor's home city. e.g. "Arlington, VA" or "20001".</p>
                            </div>
                            <div>
                                <label className="label-base">Within</label>
                                <select data-testid="vendor-radius" className="input-base" value={filters.radius_miles} onChange={(e) => setFilters({ ...filters, radius_miles: parseInt(e.target.value, 10) })}>
                                    {RADIUS_OPTIONS.map((r) => (<option key={r} value={r}>{r} miles</option>))}
                                </select>
                            </div>
                            <div>
                                <label className="label-base">Category</label>
                                <select data-testid="vendor-cat" className="input-base" value={filters.category_id} onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}>
                                    <option value="">All categories</option>
                                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="flex gap-2">
                                <button data-testid="vendor-search-btn" onClick={load} className="pill btn-forest flex-1 h-10 font-semibold">Apply</button>
                                <button data-testid="vendor-reset" onClick={() => { setFilters({ q: "", category_id: "", near: "", radius_miles: 50 }); setTimeout(load, 0); }} className="pill px-4 h-10 text-sm font-semibold border border-brand-line bg-white hover:bg-brand-bone">Reset</button>
                            </div>
                        </div>
                    </div>
                </aside>

                <div className="lg:col-span-9">
                    <div className="text-sm text-brand-mute mb-4" data-testid="vendor-count">
                        {loading ? "Loading…" : `${vendors.length} vendor${vendors.length === 1 ? "" : "s"}${filters.near ? ` within ${filters.radius_miles} mi of "${filters.near}"` : ""}`}
                    </div>
                    {error && !loading && (
                        <div
                            className="mb-5 rounded-xl border border-brand-coral/40 bg-brand-coral/5 p-5 flex items-start justify-between gap-3"
                            data-testid="vendors-load-error"
                        >
                            <div>
                                <div className="font-heading font-bold text-brand-coral">Couldn't load vendors</div>
                                <p className="text-sm text-brand-ink mt-1">{error}</p>
                            </div>
                            <button
                                onClick={load}
                                data-testid="vendors-retry"
                                className="pill btn-coral px-4 py-2 text-sm font-semibold flex-shrink-0"
                            >
                                Retry
                            </button>
                        </div>
                    )}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {vendors.map((v) => (
                            <Link to={`/vendors/${v.vendor_slug || v.id}`} key={v.id} className="rounded-xl border border-brand-line bg-white card-hover overflow-hidden block" data-testid={`vendor-card-${v.id}`}>
                                <div className="aspect-[16/10] bg-brand-bone overflow-hidden relative">
                                    {v.photos && v.photos[0] ? (
                                        <img src={imageUrl(v.photos[0])} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                    ) : (
                                        <div className="w-full h-full grain" />
                                    )}
                                    {v.distance_miles != null && (
                                        <span className="absolute top-3 right-3 bg-brand-forest text-white text-xs font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1" data-testid={`vendor-distance-${v.id}`}>
                                            <Compass size={12} /> {v.distance_miles} mi
                                        </span>
                                    )}
                                </div>
                                <div className="px-6 pb-6 pt-10 relative">
                                    <div className="absolute -top-7 left-6 h-14 w-14 rounded-2xl border-2 border-white bg-brand-mint shadow-md flex items-center justify-center overflow-hidden z-10">
                                        {v.logo_url ? (
                                            <img src={imageUrl(v.logo_url)} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="font-heading font-extrabold text-brand-forest">{(v.business_name || "?").slice(0, 1).toUpperCase()}</span>
                                        )}
                                    </div>
                                    <div className="font-heading font-bold">{v.business_name}</div>
                                    <div className="text-xs text-brand-mute">{v.service_area || [v.home_city, v.home_state].filter(Boolean).join(", ") || "—"}</div>
                                    <p className="text-sm text-brand-mute mt-3 line-clamp-2">{v.description || "Professional healthcare partner ready to engage your team."}</p>
                                    {/* Trust metrics row */}
                                    <VendorTrustRow trust={v.trust} />
                                    <div className="flex flex-wrap gap-1.5 mt-4">
                                        {(v.categories || []).slice(0, 3).map((cid) => <span key={cid} className="chip">{catName(cid)}</span>)}
                                    </div>
                                    {v.freebies && (
                                        <div className="mt-3 rounded-lg bg-brand-bone px-3 py-2 flex items-start gap-2" data-testid={`vendor-card-freebies-${v.id}`}>
                                            <Gift size={13} className="text-brand-coral mt-0.5 shrink-0" />
                                            <div className="text-xs text-brand-ink leading-snug line-clamp-2"><span className="font-semibold">Booth Freebies:</span> {v.freebies}</div>
                                        </div>
                                    )}
                                    {v.raffle_prize && (
                                        <div className="mt-2 rounded-lg bg-brand-coral/10 px-3 py-2 flex items-start gap-2" data-testid={`vendor-card-raffle-prize-${v.id}`}>
                                            <Trophy size={13} className="text-brand-coral mt-0.5 shrink-0" />
                                            <div className="text-xs text-brand-ink leading-snug line-clamp-2"><span className="font-semibold">Raffle prize:</span> {v.raffle_prize}</div>
                                        </div>
                                    )}
                                    {(() => {
                                        const _raw = v.interactive_booth_elements;
                                        const ibe = Array.isArray(_raw)
                                            ? _raw.map(elementName).filter(Boolean)
                                            : (typeof _raw === "string" && _raw.trim()
                                                ? _raw.split(",").map((s) => s.trim()).filter(Boolean)
                                                : []);
                                        if (ibe.length === 0) return null;
                                        return (
                                            <div className="mt-2" data-testid={`vendor-card-ibe-${v.id}`}>
                                                <div className="text-[10px] uppercase tracking-wider text-brand-mute font-bold flex items-center gap-1">
                                                    <Sparkles size={10} className="text-brand-forest" /> Booth elements
                                                </div>
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {ibe.slice(0, 3).map((name) => (
                                                        <span key={name} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-brand-mint text-brand-forest">{name}</span>
                                                    ))}
                                                    {ibe.length > 3 && <span className="text-[11px] text-brand-mute">+{ibe.length - 3} more</span>}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </Link>
                        ))}
                        {!loading && vendors.length === 0 && (
                            <div className="sm:col-span-2 lg:col-span-3 rounded-xl border border-dashed border-brand-line p-12 text-center text-brand-mute">
                                {filters.near
                                    ? <>No vendors within {filters.radius_miles} miles of <span className="font-semibold">"{filters.near}"</span>. Try a wider radius or remove the location filter.</>
                                    : "No vendors yet."}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Browse by category + city — internal-link tower for SEO landing pages. */}
            {(directory.categories.length > 0 || directory.cities.length > 0) && (
                <section className="border-t border-brand-line bg-brand-bone/60">
                    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-2 gap-10">
                        {directory.categories.length > 0 && (
                            <div data-testid="browse-by-category">
                                <div className="label-base">Browse by category</div>
                                <h2 className="font-heading text-2xl font-bold mt-1">Vendor categories</h2>
                                <div className="grid sm:grid-cols-2 gap-2 mt-4">
                                    {directory.categories.filter((c) => c.vendor_count > 0).map((c) => (
                                        <Link key={c.id} to={`/vendors/${c.slug}`}
                                              data-testid={`browse-cat-${c.slug}`}
                                              className="rounded-xl border border-brand-line bg-white p-3 hover:border-brand-forest hover:bg-brand-mint/40 transition flex items-baseline justify-between gap-3">
                                            <span className="font-semibold">{c.name}</span>
                                            <span className="text-xs text-brand-mute font-bold">{c.vendor_count}</span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                        {directory.cities.length > 0 && (
                            <div data-testid="browse-by-city">
                                <div className="label-base">Browse by city</div>
                                <h2 className="font-heading text-2xl font-bold mt-1">Top metros</h2>
                                <div className="grid sm:grid-cols-2 gap-2 mt-4">
                                    {directory.cities.map((c) => (
                                        <Link key={c.slug} to={`/vendors/${c.slug}`}
                                              data-testid={`browse-city-${c.slug}`}
                                              className="rounded-xl border border-brand-line bg-white p-3 hover:border-brand-forest hover:bg-brand-mint/40 transition flex items-baseline justify-between gap-3">
                                            <span className="font-semibold">{c.city}{c.state ? `, ${c.state}` : ""}</span>
                                            <span className="text-xs text-brand-mute font-bold">{c.vendor_count}</span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}
        </Shell>
    );
}

// ----------------------------------------------------------------------------- 
// Compact trust metrics row — appears on each marketplace card.
//   • If approved_count >= 3: star average + "(N reviews)" + would-invite badge.
//   • If approved_count <  3: "New vendor" pill (no premature rating).
// Always shows health_fairs_attended when > 0.
// ----------------------------------------------------------------------------- 
export const VendorTrustRow = ({ trust }) => {
    if (!trust) return null;
    const { health_fairs_attended: fairs, rating } = trust;
    return (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs" data-testid="vendor-trust-row">
            {fairs > 0 && (
                <span className="inline-flex items-center gap-1 font-semibold text-brand-forest" data-testid="trust-fairs-attended">
                    <BadgeCheck size={12} /> {fairs} health fair{fairs === 1 ? "" : "s"} attended
                </span>
            )}
            {rating?.show_public_rating ? (
                <>
                    <span className="inline-flex items-center gap-1 font-semibold" data-testid="trust-rating">
                        <Star size={12} className="fill-amber-400 text-amber-400" />
                        {rating.avg_stars} <span className="text-brand-mute font-normal">({rating.approved_count})</span>
                    </span>
                    {rating.would_invite_pct != null && (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold" data-testid="trust-invite-again">
                            {rating.would_invite_pct}% would invite again
                        </span>
                    )}
                </>
            ) : rating?.approved_count > 0 ? (
                <span className="text-brand-mute italic" data-testid="trust-too-few-reviews">
                    {rating.approved_count} review{rating.approved_count === 1 ? "" : "s"} so far
                </span>
            ) : (
                <span className="pill px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-brand-mint text-brand-forest" data-testid="trust-new-vendor">New vendor</span>
            )}
        </div>
    );
};

